// ---------------------------------------------------------------------------
// POI ↔ route-segment mapping
// ---------------------------------------------------------------------------
// Given a route polyline and a flat list of POIs/named features, snap each
// one onto its nearest segment of the polyline so the UI can highlight
// "this stretch of the route is what produced that recommendation".
//
// Output is intentionally lightweight: just the segment index, distance
// along the route, and offset from the line. Consumers (ScoreBreakdown,
// MapView) own the visualization.
// ---------------------------------------------------------------------------

import {
  cumulativeDistances,
  nearestPointOnPolyline,
  type LngLat,
} from "@/utils/polyline";

/** A POI/feature that we can attribute to a specific stretch of the route. */
export interface PoiSegmentMatch {
  /** Display label (e.g. "Mori Point", "Blue Bottle Coffee"). */
  name: string;
  /** Coarse category — drives icon choice in the UI. */
  kind:
    | "park" | "nature" | "water" | "historic"
    | "landmark" | "cafe" | "shop" | "viewpoint";
  /** Which dimension of the score this contributed to. Used by ScoreBreakdown
   *  to bucket matches under the right factor. */
  source: "preferenceMatch" | "scenic" | "calm" | "community";
  /** The POI's own coordinate. */
  point: LngLat;
  /** Snapped point on the polyline. */
  snapped: LngLat;
  /** Index of the polyline segment (between coords[i] and coords[i+1]). */
  segmentIndex: number;
  /** Cumulative distance along the route (m) at the snap point. */
  along: number;
  /** Perpendicular distance from POI to the polyline (m). */
  distance: number;
}

interface PoiInput {
  name: string;
  point: LngLat;
  kind: PoiSegmentMatch["kind"];
  source: PoiSegmentMatch["source"];
}

/**
 * Snap each POI onto the nearest polyline segment, dropping any that fall
 * outside `maxDistanceM` of the route line. Sorted by `along` so the UI
 * can render them in trip order.
 */
export function mapPoisToSegments(
  coords: LngLat[],
  pois: PoiInput[],
  maxDistanceM = 250,
): PoiSegmentMatch[] {
  if (coords.length < 2 || pois.length === 0) return [];
  const cum = cumulativeDistances(coords);
  const out: PoiSegmentMatch[] = [];
  for (const p of pois) {
    if (!p.name || !p.point) continue;
    const snap = nearestPointOnPolyline(p.point, coords, cum);
    if (snap.distance > maxDistanceM) continue;
    out.push({
      name: p.name,
      kind: p.kind,
      source: p.source,
      point: p.point,
      snapped: snap.snapped,
      segmentIndex: snap.segmentIndex,
      along: snap.along,
      distance: snap.distance,
    });
  }
  return out.sort((a, b) => a.along - b.along);
}

/**
 * Bucket matches by score source so callers (e.g. ScoreBreakdown) can list
 * which POIs contributed to each factor without re-iterating.
 */
export function groupBySource(
  matches: PoiSegmentMatch[],
): Record<PoiSegmentMatch["source"], PoiSegmentMatch[]> {
  const empty: Record<PoiSegmentMatch["source"], PoiSegmentMatch[]> = {
    preferenceMatch: [], scenic: [], calm: [], community: [],
  };
  for (const m of matches) empty[m.source].push(m);
  return empty;
}

/**
 * Group matches into contiguous stretches of segment indices so the UI can
 * highlight a single polyline range covering a cluster of related POIs.
 * `gap` is the maximum allowed segment-index gap to keep two matches in
 * the same range.
 */
export function groupBySegmentRange(
  matches: PoiSegmentMatch[],
  gap = 8,
): { startIndex: number; endIndex: number; matches: PoiSegmentMatch[] }[] {
  if (matches.length === 0) return [];
  const sorted = [...matches].sort((a, b) => a.segmentIndex - b.segmentIndex);
  const ranges: { startIndex: number; endIndex: number; matches: PoiSegmentMatch[] }[] = [];
  let cur = { startIndex: sorted[0].segmentIndex, endIndex: sorted[0].segmentIndex, matches: [sorted[0]] };
  for (let i = 1; i < sorted.length; i++) {
    const m = sorted[i];
    if (m.segmentIndex - cur.endIndex <= gap) {
      cur.endIndex = m.segmentIndex;
      cur.matches.push(m);
    } else {
      ranges.push(cur);
      cur = { startIndex: m.segmentIndex, endIndex: m.segmentIndex, matches: [m] };
    }
  }
  ranges.push(cur);
  return ranges;
}

/** Format a meters-along value as a friendly mile-marker for the UI. */
export function formatAlong(meters: number, totalMeters: number): string {
  if (totalMeters <= 0) return "";
  const pct = Math.round((meters / totalMeters) * 100);
  const km = meters / 1000;
  return km < 1 ? `${Math.round(meters)} m in (${pct}%)` : `${km.toFixed(1)} km in (${pct}%)`;
}
